import {DatePicker, Input, InputNumber, Select, Switch, Tooltip} from 'antd';
import {useMemo} from 'react';
import moment from 'moment';
import {IdField} from './IdField';
import {fieldHelp, humanizeEnum, humanizeField} from './constants';
import {
  instrumentTypeOptions,
  useAssetClassOptions,
} from './useAssetClassOptions';
import styles from './themes.module.scss';

/**
 * Renders one filter field from its metadata descriptor.
 *
 * The control is chosen from `type` / `enumValues` / `isList`, so whatever the
 * structure endpoint returns is editable. `value`/`onChange` are the raw form
 * value for this field.
 *
 * Two fields are named explicitly, because their real options are data rather
 * than schema: `assetClassKey` lists the asset classes that have actually been
 * created, and `instrumentKey` lists the types that live under the one chosen.
 * The submitted values are the same enum members either way.
 */
export const FilterField = ({
  field,
  feature,
  branch,
  compact,
  value,
  onChange,
  disabled,
}) => {
  const label = humanizeField(field.name);
  const {options: assetClassOptions, loading: loadingAssetClasses} =
    useAssetClassOptions(
      field.name === 'assetClassKey' ? field.enumValues : [],
    );
  const typeOptions = useMemo(
    () =>
      field.name === 'instrumentKey'
        ? instrumentTypeOptions(branch?.assetClassKey, field.enumValues)
        : [],
    [field.name, field.enumValues, branch?.assetClassKey],
  );

  const control = () => {
    if (field.name === 'assetClassKey') {
      return (
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          disabled={disabled}
          loading={loadingAssetClasses}
          value={value}
          placeholder={
            assetClassOptions.length
              ? 'Select asset class'
              : 'No asset classes created'
          }
          options={assetClassOptions}
          onChange={onChange}
        />
      );
    }

    if (field.name === 'instrumentKey') {
      return (
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          // The types on offer depend on the asset class, so there is nothing
          // meaningful to choose from until one is picked.
          disabled={disabled || !branch?.assetClassKey}
          value={value}
          placeholder={
            branch?.assetClassKey
              ? 'Select instrument type'
              : 'Pick an asset class first'
          }
          options={typeOptions}
          onChange={onChange}
        />
      );
    }

    // Enum → single or multi select regardless of the underlying scalar type.
    if (field.enumValues?.length) {
      return (
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          mode={field.isList ? 'multiple' : undefined}
          disabled={disabled}
          value={value}
          placeholder={`Select ${label.toLowerCase()}`}
          options={field.enumValues.map(option => ({
            value: option,
            label: humanizeEnum(option),
          }))}
          onChange={onChange}
        />
      );
    }

    if (field.type === 'Boolean') {
      // Explicit false is meaningful (e.g. `explore`), so this is always "set".
      return (
        <Switch
          disabled={disabled}
          checked={Boolean(value)}
          onChange={onChange}
        />
      );
    }

    if (field.type === 'ID') {
      return (
        <IdField
          field={field}
          branch={branch}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      );
    }

    if (field.type === 'Int') {
      if (field.isList) {
        return (
          <Select
            mode="tags"
            allowClear
            disabled={disabled}
            value={value}
            placeholder="Enter numbers"
            tokenSeparators={[',', ' ']}
            onChange={next =>
              onChange(
                (Array.isArray(next) ? next : [next])
                  .map(Number)
                  .filter(item => Number.isFinite(item)),
              )
            }
          />
        );
      }
      return (
        <InputNumber
          className={styles.fullWidth}
          disabled={disabled}
          value={value}
          precision={0}
          placeholder={label}
          onChange={onChange}
        />
      );
    }

    if (field.type === 'DateTime') {
      return (
        <DatePicker
          showTime
          className={styles.fullWidth}
          disabled={disabled}
          value={value ? moment(value) : null}
          onChange={date => onChange(date ? date.toISOString() : undefined)}
        />
      );
    }

    // String (and any unknown scalar) → text, or a tag input when a list.
    if (field.isList) {
      return (
        <Select
          mode="tags"
          allowClear
          disabled={disabled}
          value={value}
          placeholder="Add values"
          tokenSeparators={[',']}
          onChange={onChange}
        />
      );
    }
    return (
      <Input
        disabled={disabled}
        value={value}
        placeholder={label}
        onChange={event => onChange(event.target.value)}
      />
    );
  };

  // The structure endpoint supplies a description for 2 of the 49 fields, so
  // the rest fall back to what the integration guide documents.
  const help = field.description || fieldHelp(feature, field);

  const labelNode = (
    <label className={styles.fieldLabel}>
      {label}
      {field.required && <span className={styles.required}>*</span>}
      {help && (
        <Tooltip title={help}>
          <span className={styles.hintMark}>?</span>
        </Tooltip>
      )}
    </label>
  );

  // Compact toggles put the switch beside its label instead of under it — a
  // 44px control does not need a full grid cell of its own.
  if (compact) {
    return (
      <div className={styles.toggleRow}>
        {control()}
        {labelNode}
      </div>
    );
  }

  return (
    <div
      className={`${styles.field} ${
        field.type === 'Boolean' && !field.enumValues?.length
          ? styles.switchField
          : ''
      }`}>
      {labelNode}
      {control()}
    </div>
  );
};

export default FilterField;
